import React from 'react';

import Locale from 'data/locale';
import Data from 'data';
import Store from 'store';
import Session from 'store/session';

import './Altar.scss';
import { altarToggle } from 'logic/skills';
import { D3TooltipAltar } from 'tooltips';
import classNames from 'classnames';
import Slider from 'ui/slider';
import { BuildInfoLevel, getProfilePath } from 'common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { ProfileStore } from 'store';

const _L = Locale("ui-paragon.js");

const RowPos = [30, 60, 107, 145, 183, 220, 258, 296, 334, 373, 417];
const ColPos = [17, 55, 93, 130, 169, 208, 246, 284, 320];

function nodePos(id) {
  const node = Data.altar[id];
  const result = [ColPos[node.col], RowPos[node.row]];
  if (id === 'minor7') {
    result[0] -= 1;
    result[1] -= 1;
  } else if (id === 'minor11') {
    result[0] -= 2;
    result[1] -= 0;
  } else if (id === 'minor16') {
    result[0] -= 2;
  } else if (id === 'minor20') {
    result[0] -= 2;
  } else if (id === 'minor24') {
    result[0] -= 1;
  } else if (id === 'minor25') {
    result[0] -= 1;
  } else if (id === 'minor26') {
    result[0] += 2;
    result[1] += 1;
  }
  return result;
}

function lineState(nodes, id1, id2) {
  const i1 = nodes.includes(id1), i2 = nodes.includes(id2);
  if (i1 && i2) return 'd3p-line d3p-active';
  const d1 = Data.altar[id1], d2 = Data.altar[id2];
  if (i1 && d2.requires?.includes(id1)) return 'd3p-line';
  if (i2 && d1.requires?.includes(id2)) return 'd3p-line';
  return null;
}

function AltarLine({ id1, id2, nodes }) {
  const state = lineState(nodes, id1, id2);
  if (!state) return null;
  const [x1, y1] = nodePos(id1);
  const [x2, y2] = nodePos(id2);
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.round(Math.sqrt(dx * dx + dy * dy));
  const ang = Math.atan2(dy, dx);
  return (
    <div className={state} style={{
      left: x1,
      top: y1,
      width: len,
      transform: `rotate(${ang.toFixed(3)}rad) translateY(-1.5px)`,
    }}/>
  );
}

function AltarNode({ id, nodes, onClick }) {
  const data = Data.altar[id];
  const classNames = [`d3p-AltarNode`];
  if (data.major) classNames.push(`d3p-major`);
  if (data.final) {
    classNames.push(`d3p-final`);
    if (nodes.length >= 29) classNames.push(`d3p-active`);
  } else {
    if (nodes.includes(id)) classNames.push(`d3p-active`);
  }
  if (!nodes.includes(id) && data.requires && !data.requires.some(r => nodes.includes(r))) classNames.push(`d3p-disabled`);
  const [left, top] = nodePos(id);
  const style = {
    backgroundPositionX: `-${data.icon}em`,
    left, top,
  };
  return <>
    <D3TooltipAltar id={id}>
      <div className={classNames.join(' ')} style={style} onClick={() => onClick?.(id)}/>
    </D3TooltipAltar>
    {!!data.requires && data.requires.map(node => {
      const odata = Data.altar[node];
      if (odata.row > data.row || (odata.row === data.row && odata.col > data.col)) return null;
      return <AltarLine key={node} id1={node} id2={id} nodes={nodes}/>;
    })}
  </>;
}

function AltarDisplay({ nodes, onClick, href }) {
  const Container = href ? 'a' : 'div';
  const props = {};
  if (href) {
    props.href = href;
    props.target = "_blank";
    props.rel = "noopener noreferrer";
  }
  return (
    <Container className="d3p-AltarPanel" {...props}>
      {Object.keys(Data.altar).map(id => (
        <AltarNode key={id} id={id} nodes={nodes} onClick={onClick}/>
      ))}
    </Container>
  );
}

export function AltarEmbed({ style }) {
  const nodes = Store.useProfile(profile => profile.altar) || [];
  const [position, setPosition] = React.useState(nodes.length);

  const onLeft = React.useCallback(() => setPosition(pos => Math.max(1, pos - 1)), []);
  const onRight = React.useCallback(() => setPosition(pos => Math.min(nodes.length, pos + 1)), [nodes.length]);

  return (
    <div className="d3p-AltarEmbed" style={style}>
      <AltarDisplay nodes={nodes.slice(0, position)}/>
      <div className="d3p-Slider">
        <div className={classNames("d3p-button d3p-move d3p-left", {"d3p-disabled": position <= 1})} onMouseDown={onLeft}/>
        <Slider min={1} max={nodes.length} step={1} value={position} onChange={setPosition}/>
        <div className={classNames("d3p-button d3p-move d3p-right", {"d3p-disabled": position >= nodes.length})} onMouseDown={onRight}/>
      </div>
    </div>
  );
}

function copyText(text) {
  const node = document.createElement('input');
  document.body.appendChild(node);
  node.value = text;
  node.select();
  document.execCommand('copy');
  document.body.removeChild(node);
}

export function EmbedLink({ type }) {
  const isCC = Session.useSelector(state => (state.userLevel || 0) >= BuildInfoLevel);
  const state = Store.useState();
  const match = window.location.pathname.match(/\/(\d+)$/);
  const url = match && new URL(getProfilePath(parseInt(match[1])), window.location);
  if (url) {
    url.hash = `#${state.activeProfile + 1}&${type}`;
  }
  const [copied, setCopied] = React.useState(false);
  const isMounted = React.useRef(true);
  React.useEffect(() => (() => isMounted.current = false), []);
  const onClick = e => {
    e.preventDefault();
    e.stopPropagation();
    copyText(url.href);
    setCopied(true);
    setTimeout(() => isMounted.current && setCopied(false), 1000);
  };
  if (!match || !isCC) return null;
  return (
    <span className="d3p-EmbedLink" onClick={onClick}>
      {copied && 'Copied!'}
      {!copied && <FontAwesomeIcon icon={faLink}/>}
    </span>
  );
}

export function AltarSection() {
  const nodes = Store.useProfile(profile => profile.altar) || [];
  const dispatch = Store.useDispatch();
  const onClick = React.useCallback(id => dispatch.setProfile(profile => {
    if (Data.altar[id].final) return profile;
    profile = {...profile};
    profile.altar = altarToggle(profile.altar?.slice() || [], id);
    return profile;
  }), [dispatch]);

  const onFill = React.useCallback(() => dispatch.setProfile(profile => {
    profile = {...profile}
    profile.altar = profile.altar?.slice() ?? []
    for (const id of ProfileStore.initialState.altar) {
      if (!profile.altar.includes(id)) {
        profile.altar.push(id)
      }
    }
    return profile
  }), [dispatch])

  const onReset = React.useCallback(() => dispatch.setProfile(profile => {
    profile = {...profile}
    profile.altar = []
    return profile
  }), [dispatch])

  return (
    <div className="d3p-Section d3p-expanded d3p-always">
      <h3>
        {_L`Altar of Rites`}
        {' '}
        <span onClick={onFill} className={classNames("d3p-ParagonReset", { "d3p-disabled": nodes.length >= ProfileStore.initialState.altar.length })}>(Fill)</span>
        {' '}
        <span onClick={onReset} className={classNames("d3p-ParagonReset", { "d3p-disabled": !nodes.length })}>(Reset)</span>
        {' '}
        <EmbedLink type="altar"/>
      </h3>
      <AltarDisplay nodes={nodes} onClick={onClick}/>
    </div>
  );
}
